import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ImageSlider.css';

const ImageSlider = ({ images, onSelect, initialRandom = true }) => {
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [rotation, setRotation] = useState(0);
    const totalImages = images.length;
    const angleStep = 360 / totalImages;

    // Dynamically calculate radius based on container size
    const radius = Math.min(containerSize.width, containerSize.height) * 1;
    const itemSize = Math.min(containerSize.width, containerSize.height) * 0.25;

    const transparentValue = 4; // Transparency intensity

    // Calculate the selected index
    const selectedIndex = Math.round(-rotation / angleStep) % totalImages;
    const normalizedIndex = (selectedIndex + totalImages) % totalImages;

    // Direct selection of an image
    const handleImageClick = useCallback((index) => {
        // Calculate the shortest path to the selected image
        const currentPos = -rotation % 360;
        const targetPos = index * angleStep;

        // Find the difference considering the circular nature
        let diff = targetPos - currentPos;

        // Adjust for the shortest path around the circle
        if (Math.abs(diff) > 180) {
            diff = diff > 0 ? diff - 360 : diff + 360;
        }
        setRotation((prev) => prev - diff);
    }, [rotation, angleStep]);

    // Function to select a random image
    const selectRandomImage = useCallback(() => {
        const randomIndex = Math.floor(Math.random() * 10000) % images.length;
        onSelect(randomIndex);
        handleImageClick(randomIndex);
        // your image selection logic here
    }, [images.length, onSelect, handleImageClick]); // add dependencies if needed inside



    const initialSelectionDone = useRef(false);
    // Initial random selection when component mounts
    useEffect(() => {
        if (initialRandom && !initialSelectionDone.current) {
            selectRandomImage();
            initialSelectionDone.current = true;
        }
    }, [initialRandom, selectRandomImage]);

    // Update container size on mount and window resize
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight
                });
            }
        };

        updateSize();
        window.addEventListener('resize', updateSize);

        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        if (onSelect) {
            onSelect(normalizedIndex);
        }
    }, [rotation, normalizedIndex, onSelect]);

    // Calculate opacity based on distance from center
    const calculateOpacity = (index) => {
        const angleDifference = Math.abs(((index - normalizedIndex + totalImages) % totalImages));
        const distanceRatio = Math.min(angleDifference, totalImages - angleDifference) / (totalImages / 2);
        // Opacity ranges from 1 (center) to 0.2 (farthest)
        return 1 - (distanceRatio * transparentValue);
    };

    return (
        <div className="carousel" ref={containerRef}>
            <div
                className="carousel-container"
                style={{ perspective: "1000px" }}
            >
                <div
                    className="carousel-inner"
                    style={{ transform: `rotateY(${rotation}deg)` }}
                >
                    {images.map((img, index) => {
                        const angle = index * angleStep;
                        const opacity = calculateOpacity(index);
                        const isSelected = index === normalizedIndex;

                        return (
                            <div
                                key={index}
                                className={`carousel-item ${isSelected ? 'selected' : ''}`}
                                style={{
                                    transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
                                    width: `${itemSize}px`,
                                    height: `${itemSize}px`,
                                    marginLeft: `-${itemSize / 2}px`,
                                    marginTop: `-${itemSize / 2}px`
                                }}
                                onClick={() => handleImageClick(index)}
                            >
                                <img
                                    src={img}
                                    alt={`Icon ${index + 1}`}
                                    style={{
                                        opacity: opacity,
                                        filter: `brightness(${opacity * 100}%)`
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
export default ImageSlider;